export type SearchCase = {
  id: string
  title: string | null
  status: string
  filters: Record<string, unknown>
  created_at: string
}

export type AuthUser = {
  id: string
  email: string
  role: 'admin' | 'analyst' | 'viewer'
}

export type UserRole = AuthUser['role']

export type AdminUser = {
  id: string
  email: string
  role: UserRole
  created_at: string
  last_login_at: string | null
}

export type AdminUserCreate = {
  email: string
  role: UserRole
  password: string
}

export type AdminUserUpdate = {
  email?: string
  role?: UserRole
  password?: string
}

export type LoginResponse = {
  access_token: string
  token_type: 'bearer'
  user: AuthUser
}

export type PresignUpload = {
  asset_id: string
  object_key: string
  upload_url: string
  expires_seconds: number
}

export type SearchResult = {
  id: string
  trademark_serial_number: string
  registration_number: string | null
  mark_literal: string | null
  owner_name: string | null
  status_label: string
  international_classes: number[]
  goods_services: string | null
  filing_date: string | null
  registration_date: string | null
  visual_score: number
  text_score: number
  class_score: number
  status_score: number
  hash_score: number
  final_score: number
  rank: number
  source_record_url: string | null
  source_updated_at: string | null
  review_state: ReviewState
  review_note: string | null
  query_image_url: string | null
  trademark_image_url: string | null
  image_url_expires_seconds: number | null
}

export type TrademarkRecord = {
  id: string
  serial_number: string
  registration_number: string | null
  mark_literal: string | null
  mark_type: string
  status_code: string
  status_label: string
  filing_date: string | null
  registration_date: string | null
  owner_name: string | null
  owner_entity_type: string | null
  owner_country: string | null
  goods_services: string | null
  international_classes: number[]
  design_search_codes: string[]
  source_record_url: string | null
  source_updated_at: string | null
  last_synced_at: string
}

export type TrademarkSearchResponse = {
  query: string
  results: TrademarkRecord[]
}

export type ReviewState = 'related' | 'unrelated' | 'review'

export type SearchResultReviewUpdate = {
  review_state: ReviewState
  review_note: string | null
}

export type ReportDownload = {
  report_id: string
  object_key: string
  content_type: string
  download_url: string
  expires_seconds: number
}

export type AdminJob = {
  id: string
  job_type: string
  status: string
  priority: number
  attempts: number
  max_attempts: number
  run_after: string
  locked_by: string | null
  last_error: string | null
}

export type SyncCursor = {
  source: string
  cursor: Record<string, unknown>
  updated_at: string
}

export type SyncStatus = {
  status: string
  job_counts: Record<string, number>
  recent_cursors: SyncCursor[]
}

export type TrademarkSyncRequest = {
  serial_numbers: string[]
  serial_start?: string | null
  serial_end?: string | null
  required_classes?: number[]
  required_status_prefixes?: string[]
}

export type TrademarkSyncQueued = {
  status: string
  queued_count: number
  job_ids: string[]
}

export type ScoringWeights = {
  visual: number
  text: number
  class_relevance: number
  status: number
  perceptual_hash: number
}

export type ScoringSettings = ScoringWeights & {
  updated_at: string | null
  updated_by_user_id: string | null
}

export type AuditLog = {
  id: string
  actor_user_id: string | null
  action: string
  target_type: string
  target_id: string | null
  metadata: Record<string, unknown>
  created_at: string
}

const apiPrefix = '/api/v1'
const authTokenStorageKey = 'marklens.auth.token'
const r2UploadRetryDelaysMs = [500, 1500, 3000] as const

class R2UploadStatusError extends Error {
  constructor(readonly status: number) {
    super(`Upload failed with status ${status}`)
    this.name = 'R2UploadStatusError'
  }
}

export function getStoredAuthToken(): string | null {
  return window.localStorage.getItem(authTokenStorageKey)
}

export function storeAuthToken(token: string): void {
  window.localStorage.setItem(authTokenStorageKey, token)
}

export function clearStoredAuthToken(): void {
  window.localStorage.removeItem(authTokenStorageKey)
}

function apiHeaders(headers: HeadersInit = {}): Headers {
  const nextHeaders = new Headers(headers)
  const token = getStoredAuthToken()
  if (token) {
    nextHeaders.set('authorization', `Bearer ${token}`)
  }
  return nextHeaders
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    throw new Error(errorMessageFromBody(text, response.status))
  }
  return (await response.json()) as T
}

function errorMessageFromBody(body: string, status: number): string {
  if (!body) {
    return `Request failed with status ${status}`
  }
  try {
    const parsed = JSON.parse(body) as { detail?: unknown }
    if (typeof parsed.detail === 'string') {
      return parsed.detail
    }
  } catch {
    return body
  }
  return body
}

function isRetryableUploadStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500
}

function normalizeUploadError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }
  return new Error('Upload failed')
}

async function waitForUploadRetry(attempt: number): Promise<void> {
  const delayMs = r2UploadRetryDelaysMs[attempt]
  if (delayMs === undefined) {
    return
  }
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs)
  })
}

export async function login(params: { email: string; password: string }): Promise<LoginResponse> {
  const response = await fetch(`${apiPrefix}/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params)
  })
  return readJson<LoginResponse>(response)
}

export async function logout(): Promise<{ status: string }> {
  const response = await fetch(`${apiPrefix}/auth/logout`, {
    method: 'POST',
    headers: apiHeaders()
  })
  return readJson<{ status: string }>(response)
}

export async function fetchCurrentUser(): Promise<AuthUser> {
  const response = await fetch(`${apiPrefix}/auth/me`, {
    headers: apiHeaders()
  })
  return readJson<AuthUser>(response)
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const response = await fetch(`${apiPrefix}/admin/users`, {
    headers: apiHeaders()
  })
  return readJson<AdminUser[]>(response)
}

export async function createAdminUser(payload: AdminUserCreate): Promise<AdminUser> {
  const response = await fetch(`${apiPrefix}/admin/users`, {
    method: 'POST',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(payload)
  })
  return readJson<AdminUser>(response)
}

export async function updateAdminUser(params: {
  userId: string
  update: AdminUserUpdate
}): Promise<AdminUser> {
  const response = await fetch(`${apiPrefix}/admin/users/${params.userId}`, {
    method: 'PATCH',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(params.update)
  })
  return readJson<AdminUser>(response)
}

export async function fetchCases(): Promise<SearchCase[]> {
  const response = await fetch(`${apiPrefix}/cases`, {
    headers: apiHeaders()
  })
  return readJson<SearchCase[]>(response)
}

export async function createCase(title: string): Promise<SearchCase> {
  const response = await fetch(`${apiPrefix}/cases`, {
    method: 'POST',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify({ title, filters: { class: [14], status: ['LIVE'] } })
  })
  return readJson<SearchCase>(response)
}

export async function presignUpload(params: {
  caseId: string
  file: File
}): Promise<PresignUpload> {
  const response = await fetch(`${apiPrefix}/cases/${params.caseId}/uploads/presign`, {
    method: 'POST',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify({
      filename: params.file.name,
      content_type: params.file.type || 'application/octet-stream',
      size_bytes: params.file.size
    })
  })
  return readJson<PresignUpload>(response)
}

export async function uploadToR2(params: { presign: PresignUpload; file: File }): Promise<void> {
  const maxAttempts = r2UploadRetryDelaysMs.length + 1

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const isLastAttempt = attempt === maxAttempts - 1

    try {
      const response = await fetch(params.presign.upload_url, {
        method: 'PUT',
        headers: { 'content-type': params.file.type || 'application/octet-stream' },
        body: params.file
      })
      if (response.ok) {
        return
      }
      if (!isRetryableUploadStatus(response.status) || isLastAttempt) {
        throw new R2UploadStatusError(response.status)
      }
    } catch (error) {
      if (error instanceof R2UploadStatusError) {
        throw error
      }
      if (isLastAttempt) {
        throw normalizeUploadError(error)
      }
    }

    await waitForUploadRetry(attempt)
  }

  throw new Error('Upload failed after retries')
}

export async function completeUpload(params: {
  caseId: string
  assetId: string
}): Promise<{ status: string; job_id: string }> {
  const response = await fetch(
    `${apiPrefix}/cases/${params.caseId}/uploads/${params.assetId}/complete`,
    { method: 'POST', headers: apiHeaders() }
  )
  return readJson<{ status: string; job_id: string }>(response)
}

export async function runSearch(caseId: string): Promise<{ status: string }> {
  const response = await fetch(`${apiPrefix}/cases/${caseId}/search`, {
    method: 'POST',
    headers: apiHeaders()
  })
  return readJson<{ status: string }>(response)
}

export async function fetchResults(caseId: string): Promise<SearchResult[]> {
  const response = await fetch(`${apiPrefix}/cases/${caseId}/results`, {
    headers: apiHeaders()
  })
  return readJson<SearchResult[]>(response)
}

export async function createReport(caseId: string): Promise<ReportDownload> {
  const response = await fetch(`${apiPrefix}/cases/${caseId}/reports`, {
    method: 'POST',
    headers: apiHeaders()
  })
  return readJson<ReportDownload>(response)
}

export async function updateResultReview(params: {
  caseId: string
  resultId: string
  review: SearchResultReviewUpdate
}): Promise<{ status: string }> {
  const response = await fetch(`${apiPrefix}/cases/${params.caseId}/results/${params.resultId}`, {
    method: 'PATCH',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(params.review)
  })
  return readJson<{ status: string }>(response)
}

export async function fetchAdminJobs(): Promise<AdminJob[]> {
  const response = await fetch(`${apiPrefix}/admin/jobs`, {
    headers: apiHeaders()
  })
  return readJson<AdminJob[]>(response)
}

export async function retryAdminJob(jobId: string): Promise<AdminJob> {
  const response = await fetch(`${apiPrefix}/admin/jobs/${jobId}/retry`, {
    method: 'POST',
    headers: apiHeaders()
  })
  return readJson<AdminJob>(response)
}

export async function searchTrademarks(params: {
  query: string
  limit?: number
}): Promise<TrademarkSearchResponse> {
  const searchParams = new URLSearchParams()
  searchParams.set('query', params.query)
  searchParams.set('limit', String(params.limit ?? 20))
  const response = await fetch(`${apiPrefix}/trademarks/search?${searchParams.toString()}`, {
    headers: apiHeaders()
  })
  return readJson<TrademarkSearchResponse>(response)
}

export async function fetchSyncStatus(): Promise<SyncStatus> {
  const response = await fetch(`${apiPrefix}/admin/sync/status`, {
    headers: apiHeaders()
  })
  return readJson<SyncStatus>(response)
}

export async function queueTrademarkSync(
  payload: TrademarkSyncRequest
): Promise<TrademarkSyncQueued> {
  const response = await fetch(`${apiPrefix}/admin/trademarks/sync`, {
    method: 'POST',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(payload)
  })
  return readJson<TrademarkSyncQueued>(response)
}

export async function queueTrademarkResync(serialNumber: string): Promise<TrademarkSyncQueued> {
  const response = await fetch(`${apiPrefix}/admin/trademarks/resync/${serialNumber}`, {
    method: 'POST',
    headers: apiHeaders()
  })
  return readJson<TrademarkSyncQueued>(response)
}

export async function fetchScoringSettings(): Promise<ScoringSettings> {
  const response = await fetch(`${apiPrefix}/admin/settings/scoring`, {
    headers: apiHeaders()
  })
  return readJson<ScoringSettings>(response)
}

export async function updateScoringSettings(
  weights: ScoringWeights
): Promise<ScoringSettings> {
  const response = await fetch(`${apiPrefix}/admin/settings/scoring`, {
    method: 'PUT',
    headers: apiHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify(weights)
  })
  return readJson<ScoringSettings>(response)
}

export async function fetchAuditLogs(limit = 50): Promise<AuditLog[]> {
  const searchParams = new URLSearchParams()
  searchParams.set('limit', String(limit))
  const response = await fetch(`${apiPrefix}/admin/audit?${searchParams.toString()}`, {
    headers: apiHeaders()
  })
  return readJson<AuditLog[]>(response)
}
